import React, { forwardRef, type SVGProps, useMemo } from 'react';

import { cn } from '../../utils';

export const AUTONE_ICON_SIZES = {
  '4xs': 12,
  '3xs': 14,
  '2xs': 16,
  xs: 18,
  sm: 20,
  base: 24,
  lg: 28,
  xl: 32,
  '2xl': 48,
} as const;

export type AutoneIconSize = keyof typeof AUTONE_ICON_SIZES;

export interface AutoneIconProps
  extends Omit<SVGProps<SVGSVGElement>, 'color'> {
  /**
   * We have a range of predefined icon sizes to choose from.
   * - `4xs: 12px`
   * - `3xs: 14px`
   * - `2xs: 16px`
   * - `xs: 18px`
   * - `sm: 20px`
   * - `base: 24px (default)`
   * - `lg: 28px`
   * - `xl: 32px`
   * - `2xl: 48px`
   */
  size?: AutoneIconSize;
}

export interface AutoneIconComponent
  extends React.ForwardRefExoticComponent<
    Omit<AutoneIconProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  > {
  $$iconTag?: string;
}

export const createIconComponent = (
  name: string,
  SvgElement: React.ReactElement<React.JSX.IntrinsicElements['svg']>,
  tag?: string,
) => {
  const component: AutoneIconComponent = forwardRef<
    SVGSVGElement,
    AutoneIconProps
  >(({ size = 'base', className, ...svgProps }, ref) => {
    const sizePx = useMemo(() => AUTONE_ICON_SIZES[size], [size]);

    return React.cloneElement(SvgElement, {
      className: cn('!fill-none', className),
      width: sizePx,
      height: sizePx,
      viewBox: '0 0 24 24',
      fill: 'none',
      ref,
      ...svgProps,
    });
  });

  component.displayName = name;
  component.$$iconTag = tag;
  return component;
};
